import { createApi } from '@reduxjs/toolkit/query/react';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
// import type { RootState } from '../store';

// ========================================
// REUSABLE CACHE CONFIGURATIONS
// ========================================

/**
 * Predefined cache durations for different data types
 * Developers can use these or define custom durations
 */
export const CACHE_DURATIONS = {
  // Static/Semi-static data
  VERY_LONG: 24 * 60 * 60,     // 24 hours - for countries, industries
  LONG: 12 * 60 * 60,          // 12 hours - for dropdown data
  MEDIUM: 60 * 60,             // 1 hour - for user preferences
  SHORT: 5 * 60,               // 5 minutes - for user profile
  VERY_SHORT: 60,              // 1 minute - for usage data, real-time info

  // Custom durations
  CUSTOM: (minutes: number) => minutes * 60,
  CUSTOM_HOURS: (hours: number) => hours * 60 * 60,
} as const;

/**
 * Polling intervals for real-time data
 */
export const POLLING_INTERVALS = {
  DISABLED: 0,
  REAL_TIME: 30 * 1000,        // 30 seconds
  FREQUENT: 60 * 1000,         // 1 minute
  MODERATE: 5 * 60 * 1000,     // 5 minutes
  OCCASIONAL: 15 * 60 * 1000,  // 15 minutes

  CUSTOM: (seconds: number) => seconds * 1000,
} as const;

// ========================================
// REUSABLE TAG TYPES
// ========================================

/**
 * Common tag types that can be reused across different APIs
 * Add more as needed for your application
 */
export const COMMON_TAGS = {
  // User-related data
  USER: 'User',
  USER_PROFILE: 'UserProfile',
  USER_PREFERENCES: 'UserPreferences',

  // Static data
  COUNTRIES: 'Countries',
  INDUSTRIES: 'Industries',
  SENIORITY_LEVELS: 'SeniorityLevels',
  FUNCTIONAL_LEVELS: 'FunctionalLevels',
  COMPANY_SIZES: 'CompanySizes',

  // Dynamic data
  USAGE: 'Usage',
  EXPORTS: 'Exports',
  TEAM_PLAN: 'TeamPlan',

  // Payment-related data
  PAYMENT: 'Payment',
  SUBSCRIPTION: 'Subscription',
  PLAN: 'Plan',

  // Relationships
  CONTACT_PREF: 'ContactPref',
  COMPANY_PREF: 'CompanyPref',
} as const;

export const tagTypes = [
  // ...your existing tags...
  'UserCredits',
  'ClaimableRewards',
] as const;

// ========================================
// REUSABLE ENDPOINT CONFIGURATIONS
// ========================================

/**
 * Common endpoint configurations that can be reused
 */
export const ENDPOINT_CONFIGS = {
  /**
   * Configuration for static dropdown data (countries, industries, etc.)
   */
  STATIC_DROPDOWN: {
    keepUnusedDataFor: CACHE_DURATIONS.VERY_LONG,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  },

  /**
   * Configuration for user-specific data that changes occasionally
   */
  USER_DATA: {
    keepUnusedDataFor: CACHE_DURATIONS.MEDIUM,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  },

  /**
   * Configuration for frequently changing data
   */
  DYNAMIC_DATA: {
    keepUnusedDataFor: CACHE_DURATIONS.VERY_SHORT,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  },

  /**
   * Configuration for real-time data with polling
   */
  REAL_TIME: (pollingInterval: number = POLLING_INTERVALS.FREQUENT) => ({
    keepUnusedDataFor: CACHE_DURATIONS.VERY_SHORT,
    pollingInterval,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  }),
} as const;

// ========================================
// UTILITY FUNCTIONS
// ========================================

/**
 * Helper function to create tag providers for list queries
 */
export function providesList<T extends { id: string | number }>(
  tagType: string,
  results: T[] | undefined
) {
  return results
    ? [
        { type: tagType, id: 'LIST' },
        ...results.map(({ id }) => ({ type: tagType, id })),
      ]
    : [{ type: tagType, id: 'LIST' }];
}

/**
 * Helper function to create tag providers for single item queries
 */
export function providesItem(tagType: string, id: string | number) {
  return [{ type: tagType, id }];
}

/**
 * Helper function to create tag invalidation for mutations
 */
export function invalidatesList(tagType: string, id?: string | number) {
  return id
    ? [{ type: tagType, id }, { type: tagType, id: 'LIST' }]
    : [{ type: tagType, id: 'LIST' }];
}

// ========================================
// ERROR HANDLING UTILITIES
// ========================================

/**
 * Standard error transformer for consistent error handling
 */
export function transformErrorResponse(error: FetchBaseQueryError): string {
  if ('status' in error) {
    if (error.status === 'FETCH_ERROR') {
      return 'Network error - please check your connection';
    }
    if (error.status === 'TIMEOUT_ERROR') {
      return 'Request timeout - please try again';
    }
    if (typeof error.status === 'number') {
      return `Server error (${error.status}): ${
        (error.data as any)?.message || 'Something went wrong'
      }`;
    }
  }
  return (error as any)?.error || 'An unexpected error occurred';
}

// ========================================
// TYPESCRIPT UTILITIES
// ========================================

/**
 * Generic types for common API response patterns
 */
export interface ListResponse<T> {
  data: T[];
  total?: number;
  page?: number;
  limit?: number;
}

export interface ItemResponse<T> {
  data: T;
  message?: string;
}

export interface ErrorResponse {
  message: string;
  code?: string;
  details?: any;
}

/**
 * Helper type to extract the result type from a query
 */
export type QueryResult<T> = T extends { data: infer R } ? R : never;
